// TimelineComponents.js - VERSION CORRIGÉE avec chevron d'expansion
import React, { useState, useCallback } from 'react';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import {
    StatusSelector,
    InlineTextEditor,
    DateEditor,
    SubtaskItem
} from './TimelineUtilities';
import { getPriorityStyles, ObjectiveCard, MonthIndicator } from './TimelineHelpers';
import PrioritySelector from './PrioritySelector';

// 🔧 Composant TaskCard avec chevron d'expansion
const TaskCard = ({ task, onRefresh, allTimelineData, hooks, updateTaskStatus }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    // Extraire updatePlan des hooks
    const { plansCachedHook } = hooks || {};
    const { updatePlan, loading = false, getPlanById } = plansCachedHook || {};

    // Gérer le changement de statut
    const handleStatusChange = useCallback(async (newStatus) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index || 0;
            
            if (!planId) {
                console.error('❌ ID du plan manquant');
                alert('Erreur: ID du plan manquant');
                return;
            }

            if (!updateTaskStatus) {
                console.error('❌ Fonction updateTaskStatus non disponible');
                alert('Erreur: Fonction de mise à jour non disponible');
                return;
            }

            console.log('🔄 Mise à jour statut tâche:', { planId, taskIndex, newStatus });
            
            const result = await updateTaskStatus(planId, taskIndex, newStatus);
            
            if (result && result.success) {
                console.log('✅ Statut mis à jour avec succès');
                if (onRefresh) onRefresh();
            } else {
                throw new Error(result?.error || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour du statut:', error);
            alert('Erreur lors de la mise à jour du statut: ' + error.message);
        }
    }, [updateTaskStatus, task.data, onRefresh]);

    // Gérer le changement de statut des sous-tâches
    const handleSubtaskStatusChange = useCallback(async (subtaskIndex, newStatus) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index || 0;
            
            if (!planId || !updateTaskStatus) {
                console.error('❌ Données manquantes pour sous-tâche');
                return;
            }

            console.log('🔄 Mise à jour statut sous-tâche:', { planId, taskIndex, subtaskIndex, newStatus });
            
            const result = await updateTaskStatus(planId, taskIndex, newStatus, subtaskIndex);
            
            if (result && result.success) {
                console.log('✅ Statut sous-tâche mis à jour avec succès');
                if (onRefresh) onRefresh();
            } else {
                throw new Error(result?.error || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour du statut de la sous-action:', error);
            alert('Erreur lors de la mise à jour du statut de la sous-action: ' + error.message);
        }
    }, [updateTaskStatus, task.data, onRefresh]);

    // Gérer le changement de titre
    const handleTitleChange = useCallback(async (newTitle) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || taskIndex === undefined || !updatePlan) {
                console.error('❌ Données manquantes pour mise à jour titre');
                return;
            }

            console.log('🔄 Mise à jour titre tâche:', { planId, taskIndex, newTitle });

            const updatedTask = {
                title: newTitle.trim(),
                description: task.data?.description || '',
                status: task.data?.status || 'a_faire',
                priority: task.data?.priority || 'medium',
                deadline: task.data?.deadline || null,
                estimated_hours: task.data?.estimated_hours || null,
                subtasks: task.subtasks ? task.subtasks.map(st => ({
                    title: st.title,
                    status: st.status === 'À faire' ? 'a_faire' : 
                           st.status === 'En cours' ? 'en_cours' :
                           st.status === 'Fait' ? 'fait' : 
                           st.status === 'Annulé' ? 'annule' : 'a_faire',
                    description: '',
                    priority: 'medium'
                })) : []
            };

            const result = await updatePlan(planId, { 
                tasks: [updatedTask],
                task_index: taskIndex
            });
            
            if (result.success) {
                console.log('✅ Titre mis à jour avec succès');
                if (onRefresh) onRefresh();
            } else {
                throw new Error(result.error || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour du titre:', error);
            alert('Erreur lors de la mise à jour du titre: ' + error.message);
        }
    }, [updatePlan, task.data, task.subtasks, onRefresh]);

    // Gérer le changement de priorité
    const handlePriorityChange = useCallback(async (newPriority) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || taskIndex === undefined || !updatePlan) {
                console.error('❌ Données manquantes pour mise à jour priorité');
                return;
            }

            console.log('🔄 Mise à jour priorité tâche:', { planId, taskIndex, newPriority });

            const updatedTask = {
                title: task.title,
                description: task.data?.description || '',
                status: task.data?.status || 'a_faire',
                priority: newPriority,
                deadline: task.data?.deadline || null,
                estimated_hours: task.data?.estimated_hours || null,
                subtasks: task.subtasks ? task.subtasks.map(st => ({
                    title: st.title,
                    status: st.status === 'À faire' ? 'a_faire' : 
                           st.status === 'En cours' ? 'en_cours' :
                           st.status === 'Fait' ? 'fait' : 
                           st.status === 'Annulé' ? 'annule' : 'a_faire',
                    description: '',
                    priority: 'medium'
                })) : []
            };

            const result = await updatePlan(planId, { 
                tasks: [updatedTask],
                task_index: taskIndex
            });
            
            if (result.success) {
                console.log('✅ Priorité mise à jour avec succès');
                if (onRefresh) onRefresh();
            } else {
                throw new Error(result.error || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour de la priorité:', error);
            alert('Erreur lors de la mise à jour de la priorité: ' + error.message);
        }
    }, [updatePlan, task.data, task.title, task.subtasks, onRefresh]);
    
    // Gérer le changement de date
    const handleDateChange = useCallback(async (newDate) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || taskIndex === undefined || !updatePlan) {
                console.error('❌ Données manquantes pour mise à jour date');
                return;
            }

            console.log('🔄 Mise à jour date tâche:', { planId, taskIndex, newDate });

            const updatedTask = {
                title: task.title,
                description: task.data?.description || '',
                status: task.data?.status || 'a_faire',
                priority: task.data?.priority || 'medium',
                deadline: newDate,
                estimated_hours: task.data?.estimated_hours || null,
                subtasks: task.subtasks ? task.subtasks.map(st => ({
                    title: st.title,
                    status: st.status === 'À faire' ? 'a_faire' : 
                           st.status === 'En cours' ? 'en_cours' :
                           st.status === 'Fait' ? 'fait' : 
                           st.status === 'Annulé' ? 'annule' : 'a_faire',
                    description: '',
                    priority: 'medium'
                })) : []
            };

            const result = await updatePlan(planId, { 
                tasks: [updatedTask],
                task_index: taskIndex
            });
            
            if (result.success) {
                console.log('✅ Date mise à jour avec succès');
                if (onRefresh) onRefresh();
            } else {
                throw new Error(result.error || 'Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour de la date:', error);
            alert('Erreur lors de la mise à jour de la date: ' + error.message);
        }
    }, [updatePlan, task.data, task.title, task.subtasks, onRefresh]);

    // Fonctions pour gérer les sous-tâches
    const handleSubtaskTitleChange = useCallback(async (subtaskIndex, newTitle) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || !updatePlan || !getPlanById) return;

            const planResult = await getPlanById(planId);
            if (!planResult.success || !planResult.data?.plan_action) return;

            const currentPlan = planResult.data.plan_action;
            let currentTasks = [];

            if (typeof currentPlan.tasks === 'string') {
                try {
                    currentTasks = JSON.parse(currentPlan.tasks);
                } catch (e) {
                    currentTasks = [];
                }
            } else if (Array.isArray(currentPlan.tasks)) {
                currentTasks = [...currentPlan.tasks];
            }
            
            if (currentTasks[taskIndex]?.subtasks?.[subtaskIndex]) {
                currentTasks[taskIndex].subtasks[subtaskIndex] = {
                    ...currentTasks[taskIndex].subtasks[subtaskIndex],
                    title: newTitle.trim()
                };
                
                const result = await updatePlan(planId, { tasks: currentTasks });
                
                if (result.success && onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour du titre de la sous-tâche:', error);
        }
    }, [updatePlan, task.data, onRefresh, getPlanById]);

    const handleSubtaskDelete = useCallback(async (subtaskIndex) => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || !updatePlan || !getPlanById) return;

            const planResult = await getPlanById(planId);
            if (!planResult.success || !planResult.data?.plan_action) return;

            const currentPlan = planResult.data.plan_action;
            let currentTasks = [];

            if (typeof currentPlan.tasks === 'string') {
                try {
                    currentTasks = JSON.parse(currentPlan.tasks);
                } catch (e) {
                    currentTasks = [];
                }
            } else if (Array.isArray(currentPlan.tasks)) {
                currentTasks = [...currentPlan.tasks];
            }
            
            if (currentTasks[taskIndex]?.subtasks) {
                currentTasks[taskIndex].subtasks.splice(subtaskIndex, 1);
                
                const result = await updatePlan(planId, { tasks: currentTasks });
                
                if (result.success && onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('❌ Erreur lors de la suppression de la sous-tâche:', error);
        }
    }, [updatePlan, task.data, onRefresh, getPlanById]);

    const handleAddSubtask = useCallback(async () => {
        try {
            const planId = task.data?.id_plan;
            const taskIndex = task.data?.task_index;
            
            if (!planId || !updatePlan || !getPlanById) return;

            const planResult = await getPlanById(planId);
            if (!planResult.success || !planResult.data?.plan_action) return;

            const currentPlan = planResult.data.plan_action;
            let currentTasks = [];

            if (typeof currentPlan.tasks === 'string') {
                try {
                    currentTasks = JSON.parse(currentPlan.tasks);
                } catch (e) {
                    currentTasks = [];
                }
            } else if (Array.isArray(currentPlan.tasks)) {
                currentTasks = [...currentPlan.tasks];
            }
            
            if (currentTasks[taskIndex]) {
                if (!currentTasks[taskIndex].subtasks) {
                    currentTasks[taskIndex].subtasks = [];
                }
                
                currentTasks[taskIndex].subtasks.push({
                    title: 'Nouvelle sous-action',
                    status: 'a_faire',
                    description: '',
                    priority: 'medium'
                });
                
                const result = await updatePlan(planId, { tasks: currentTasks });
                
                if (result.success && onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('❌ Erreur lors de l\'ajout de la sous-tâche:', error);
        }
    }, [updatePlan, task.data, onRefresh, getPlanById]);

    // Trouver la catégorie de l'objectif
    const findObjectiveCategory = useCallback((objectiveName) => {
        for (const monthData of allTimelineData) {
            const objective = monthData.milestones.find(milestone =>
                milestone.type === 'objective' && milestone.title === objectiveName
            );
            if (objective && objective.data && objective.data.category) {
                return objective.data.category;
            }
        }
        return null;
    }, [allTimelineData]);

    const objectiveName = task.tag || task.objectiveName || task.objective;
    const objectiveCategory = findObjectiveCategory(objectiveName);
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;
    const subtaskCount = task.subtasks ? task.subtasks.length : 0;

    // Obtenir la priorité de la tâche et ses styles
    const taskPriority = task.data?.priority || 'medium';
    const priorityStyles = getPriorityStyles(taskPriority);

    // Fonction pour gérer le clic sur le chevron d'expansion
    const handleExpandToggle = useCallback((e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    }, [isExpanded]);

    // Gérer l'édition du titre
    const handleTitleEdit = useCallback(() => {
        setIsEditingTitle(true);
    }, []);

    const handleTitleEditComplete = useCallback(async (newTitle) => {
        setIsEditingTitle(false);
        if (newTitle !== task.title) {
            await handleTitleChange(newTitle);
        }
    }, [task.title, handleTitleChange]);

    // Toujours afficher le chevron pour permettre l'ajout de contenu
    const hasExpandableContent = true;

    return (
        <div className="bg-white rounded-lg border border-gray-300 hover:shadow-sm transition-all duration-200 group">
            <div className="p-3">
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                        {/* Icône de statut avec trait vertical */}
                        <div className="flex items-center space-x-2">
                            <div className="interactive-element mt-0.5 flex items-center justify-center">
                                <StatusSelector
                                    status={task.status}
                                    onChange={handleStatusChange}
                                    disabled={loading}
                                />
                            </div>
                            <div className="w-px h-12 bg-gray-300"></div>
                        </div>

                        <div className="min-w-0 flex-1">
                            {/* Titre éditable */}
                            <div className="flex items-center space-x-2 mb-1">
                                <div className={`interactive-element ${isEditingTitle ? 'w-full' : 'inline-block'}`}>
                                    <InlineTextEditor
                                        value={task.title}
                                        onChange={handleTitleEditComplete}
                                        onEditStart={handleTitleEdit}
                                        placeholder="Titre de l'action"
                                        className="text-sm font-medium text-gray-900 group-hover:text-teal-600"
                                        style={!isEditingTitle ? {
                                            fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                                            width: `${Math.max(task.title.length * 8, 100)}px`,
                                            minWidth: '100px',
                                            maxWidth: '400px'
                                        } : {
                                            fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                                            width: '100%'
                                        }}
                                        autoExpand={false}
                                    />
                                </div>
                            </div>

                            {/* Étiquettes et date */}
                            <div className="flex items-center flex-wrap gap-2">
                                {/* Catégorie */}
                                {objectiveCategory && (
                                    <span className="text-xs px-2 py-0.5 rounded flex-shrink-0 bg-gray-100 text-gray-600 border border-gray-200 font-medium">
                                        {objectiveCategory}
                                    </span>
                                )}

                                {/* Objectif */}
                                {objectiveName && (
                                    <span className="text-xs px-1 py-0.5 rounded flex-shrink-0 bg-gray-50 text-gray-600 border border-gray-200 font-light" style={{ fontSize: '10px' }}>
                                        {objectiveName}
                                    </span>
                                )}

                                {/* Priorité - Modifiable avec même style que objectif */}
                                <div className="interactive-element">
                                    <PrioritySelector
                                        priority={taskPriority}
                                        onChange={handlePriorityChange}
                                        disabled={loading}
                                    />
                                </div>

                                {/* Date éditable */}
                                <div className="interactive-element">
                                    <DateEditor
                                        value={task.data?.deadline}
                                        onChange={handleDateChange}
                                    />
                                </div>

                                {/* Estimation durée */}
                                {task.data?.estimated_hours && (
                                    <span className="text-xs text-gray-500 font-medium">
                                        {task.data.estimated_hours}h
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section droite avec compteur et chevron */}
                    <div className="flex-shrink-0 ml-2 flex items-center space-x-2">
                        {/* Nombre de sous-actions */}
                        {subtaskCount > 0 && (
                            <div className="mt-0.5">
                                <div className="w-5 h-5 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                                    <span className="text-xs text-gray-600 font-medium">
                                        {subtaskCount}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Chevron d'expansion - toujours affiché */}
                        <button
                            onClick={handleExpandToggle}
                            className="interactive-element p-1 rounded hover:bg-gray-100 transition-colors mt-0.5"
                            title={isExpanded ? "Réduire" : "Développer"}
                        >
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                                isExpanded ? 'transform rotate-0' : 'transform rotate-270'
                            }`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Section expandable */}
            {isExpanded && (
                <div className="border-t bg-gray-50 border-gray-200 px-3 py-1">
                    {/* Sous-actions */}
                    <div className="flex items-center justify-between mb-1">
                        <h5 className="text-xs font-semibold text-gray-700">Sous-actions :</h5>
                        <button
                            onClick={handleAddSubtask}
                            className="interactive-element flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 px-2 py-0.5 rounded hover:bg-teal-50"
                            title="Ajouter une sous-action"
                            disabled={loading}
                        >
                            <Plus className="w-3 h-3" />
                            Ajouter
                        </button>
                    </div>
                    <div className="space-y-0.01">
                        {hasSubtasks ? task.subtasks.map((subtask, index) => (
                            <div key={index} className="interactive-element">
                                <SubtaskItem
                                    subtask={subtask}
                                    subtaskIndex={index}
                                    onStatusChange={handleSubtaskStatusChange}
                                    onTitleChange={handleSubtaskTitleChange}
                                    onDelete={handleSubtaskDelete}
                                    disabled={loading}
                                />
                            </div>
                        )) : (
                            <div className="text-xs text-gray-500 italic py-1">
                                Aucune sous-action. Cliquez sur "Ajouter" pour en créer une.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Composant principal TimelineContent
export const TimelineContent = ({ timelineData, updateTaskStatus, onRefresh, hooks }) => {
    return (
        <div className="relative min-h-full">
            {/* Ligne de temps principale */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5" style={{ background: 'linear-gradient(to bottom, #016666, #016666)' }} />
            
            <div className="space-y-3 pb-4">
                {timelineData.map((monthData, monthIndex) => (
                    <div key={monthIndex} className="relative flex items-start space-x-4">
                        <MonthIndicator month={monthData.month} />
                        
                        <div className="flex-1 space-y-2 min-w-0">
                            {monthData.milestones.map((milestone, index) => (
                                <div key={milestone.id || index} className="relative pl-2">
                                    {/* Point sur la ligne de temps */}
                                    <div className="absolute -left-8 top-4 w-2 h-2 bg-teal-500 rounded-full border-2 border-white shadow-sm" />
                                    
                                    {/* Carte du milestone */}
                                    {milestone.type === 'objective' ? (
                                        <ObjectiveCard milestone={milestone} />
                                    ) : (
                                        <TaskCard
                                            task={milestone}
                                            onRefresh={onRefresh}
                                            allTimelineData={timelineData}
                                            hooks={hooks}
                                            updateTaskStatus={updateTaskStatus}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};